import axios from 'axios';
import apiClient from './apiClient';

interface ApiResponse<T> {
  code: number | string;
  message: string;
  result: T;
}

interface CategoryDto {
  id: number;
  name: string;
  description?: string;
  createAt: string;
  updateAt: string;
}

interface CategoryListDto {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
  data: CategoryDto[];
}

export interface Category {
  id: number;
  name: string;
  description?: string;
}

export interface CreateCategoryPayload {
  name: string;
  description: string;
}

export type UpdateCategoryPayload = CreateCategoryPayload;

type CategoryAction = 'load' | 'create' | 'update' | 'delete';

class CategoryApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CategoryApiError';
  }
}

const mapCategory = (category: CategoryDto): Category => ({
  id: category.id,
  name: category.name,
  description: category.description,
});

const normalizeCategoryError = (error: unknown, action: CategoryAction): CategoryApiError => {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return new CategoryApiError('Không thể kết nối đến máy chủ nhóm hàng. Vui lòng thử lại.');
    }

    if ((action === 'create' || action === 'update') && error.response.status === 409) {
      return new CategoryApiError('Nhóm hàng này đã tồn tại.');
    }

    if ((action === 'create' || action === 'update') && error.response.status === 400) {
      return new CategoryApiError('Tên hoặc mô tả nhóm hàng không hợp lệ.');
    }

    if ((action === 'update' || action === 'delete') && error.response.status === 404) {
      return new CategoryApiError('Nhóm hàng không còn tồn tại. Vui lòng tải lại danh sách.');
    }

    if (action === 'delete' && error.response.status === 409) {
      return new CategoryApiError('Không thể xóa nhóm hàng đang được sử dụng bởi sản phẩm.');
    }
  }

  const fallbackMessages: Record<CategoryAction, string> = {
    load: 'Không tải được danh sách nhóm hàng. Vui lòng thử lại.',
    create: 'Không tạo được nhóm hàng. Vui lòng thử lại.',
    update: 'Không cập nhật được nhóm hàng. Vui lòng thử lại.',
    delete: 'Không xóa được nhóm hàng. Vui lòng thử lại.',
  };

  return new CategoryApiError(fallbackMessages[action]);
};

export const getCategories = async (): Promise<Category[]> => {
  try {
    const response = await apiClient.get<ApiResponse<CategoryListDto>>('/api/categories');
    return response.data.result.data.map(mapCategory);
  } catch (error: unknown) {
    throw normalizeCategoryError(error, 'load');
  }
};

export const createCategory = async (payload: CreateCategoryPayload): Promise<Category> => {
  try {
    const response = await apiClient.post<ApiResponse<CategoryDto>>('/api/categories', payload);
    return mapCategory(response.data.result);
  } catch (error: unknown) {
    throw normalizeCategoryError(error, 'create');
  }
};

export const updateCategory = async (id: number, payload: UpdateCategoryPayload): Promise<Category> => {
  try {
    const response = await apiClient.put<ApiResponse<CategoryDto>>(`/api/categories/${id}`, payload);
    return mapCategory(response.data.result);
  } catch (error: unknown) {
    throw normalizeCategoryError(error, 'update');
  }
};

export const deleteCategory = async (id: number): Promise<void> => {
  try {
    await apiClient.delete<ApiResponse<void>>(`/api/categories/${id}`);
  } catch (error: unknown) {
    throw normalizeCategoryError(error, 'delete');
  }
};
